import {FC, useContext, useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Box, Button, CircularProgress} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {ModuleNotAvailable} from "@buildwitt/shapeshift-common/v1/module";
import {CurriculumContext} from "@/contexts/curriculumContext";
import {VideoPlayerRef} from "@buildwitt/shapeshift-common/v1/videoPlayer/videoPlayer";
import {getModule} from "@buildwitt/shapeshift-common/v1/common";
import {ICourseCurriculumModule} from "@buildwitt/shapeshift-types";
import {LessonDataProvider} from "@buildwitt/shapeshift-common/v1/providers";
import "bitmovin-player/bitmovinplayer-ui.css";
import {LocaleContext} from "@/contexts/localeContext";
import {PageBaseContext} from "@/contexts/pageBaseContext";
import {LessonPageContent} from "./LessonPageContent";
import {AuthContext} from "@buildwitt/shapeshift-identity";

const useStyles = makeStyles()(() => ({
	lessonPage: {
	},
	goToNextLesson: {
		marginTop: "25px"
	},
	notAvailableContainer: {
		width: "100%",
		textAlign: "center"
	}
}));

export const LessonPage: FC = () => {
	const {classes} = useStyles();
	const {courseSlug} = useParams();
	const {moduleSlug: lessonSlug} = useParams();
	const {curriculumState: curriculum} = useContext(CurriculumContext);
	const [{contentContainerRef}] = useContext(PageBaseContext);
	const {jwt} = useContext(AuthContext);
	const navigate = useNavigate();
	const [firstAvailableModule, setFirstAvailableModule] = useState<ICourseCurriculumModule | false>();
	const videoRef = useRef<VideoPlayerRef | null>(null);
	const [{locale}] = useContext(LocaleContext);
	const [retryKey, setRetryKey] = useState(0);
	const previousJwt = useRef<string>();
	const hadError = useRef<boolean>(false);

	useEffect(() => {
		videoRef.current = null;
		if (contentContainerRef?.current) {
			contentContainerRef.current.scrollTo({top: 0, behavior: "smooth"});
		}
	}, [lessonSlug])

	useEffect(() => {
		if (courseSlug && lessonSlug && curriculum){
			setFirstAvailableModule(getModule(curriculum) ?? false)
		}
	}, [courseSlug, lessonSlug, curriculum])

	useEffect(() => {
		if (jwt && previousJwt.current && jwt !== previousJwt.current && hadError.current) {
			hadError.current = false;
			setRetryKey(prev => prev + 1);
		}
		previousJwt.current = jwt;
	}, [jwt]);

	return (
		<Box className={classes.lessonPage}>
			<LessonDataProvider key={retryKey} locale={locale} courseSlug={courseSlug} lessonSlug={lessonSlug} curriculum={curriculum} populate={["files", "quiz", "enrollment.videoTime"]}>
				{({lesson, apiResult}) => {

					if (!curriculum || apiResult.isLoading) {
						return (
							<Box
								display="flex"
								justifyContent="center"
								alignItems="center"
								height="100%"
								minHeight="300px"
							>
								<CircularProgress />
							</Box>
						);
					}

					if (apiResult.isError && apiResult.statusCode === 401) {
						hadError.current = true;
					}

					return <>
						{(apiResult.statusCode === 200 && lesson) && <LessonPageContent lesson={lesson} />}
						{apiResult.statusCode === 403 && <Box className={classes.notAvailableContainer}>
							<ModuleNotAvailable>
								Lessons must be completed in sequence.
							</ModuleNotAvailable>
							{firstAvailableModule && <Button
								className={classes.goToNextLesson}
								onClick={() => navigate(`/course/${courseSlug}/${firstAvailableModule?.type}/${firstAvailableModule?.slug}`)}
							>
								Go to next available lesson
							</Button>}
							{firstAvailableModule === false && <Button
								className={classes.goToNextLesson}
								onClick={() => navigate("/training-feed")}
							>
								Go back to My Learning
							</Button>}
						</Box>}
					</>
				}
				}
			</LessonDataProvider>
		</Box>
	);
}